<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { isBookingField } from 'dashboard/helper/preChat';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FieldCard from './FieldCard.vue';
import IconPicker from './IconPicker.vue';

const props = defineProps({
  preChatFields: {
    type: Array,
    default: () => [],
  },
  fieldsets: {
    type: Array,
    default: () => [],
  },
  submitButtonLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'updateField',
  'reorder',
  'addFieldset',
  'removeFieldset',
  'updateFieldsetLabel',
  'updateFieldsetIcon',
  'removeField',
  'addField',
  'updateSubmitLabel',
]);

const { t } = useI18n();

const localUngrouped = ref([]);
const localBooking = ref([]);
const localSections = ref([]);
const localMessageField = ref(null);

const initLocalState = () => {
  const fieldsetIds = new Set(props.fieldsets.map(fs => fs.id));
  const enabledFields = props.preChatFields
    .filter(f => f.enabled)
    .map(f => ({ ...f }));

  const messageField = enabledFields.find(f => f.name === 'message');
  localMessageField.value = messageField
    ? { ...messageField, fieldset_id: null }
    : null;

  // Booking fields always live in the dedicated Booking section (mirrors widget)
  localBooking.value = enabledFields
    .filter(f => f.name !== 'message' && isBookingField(f))
    .map(f => ({ ...f, fieldset_id: null }));

  localUngrouped.value = enabledFields.filter(
    f =>
      f.name !== 'message' &&
      !isBookingField(f) &&
      (!f.fieldset_id || !fieldsetIds.has(f.fieldset_id))
  );
  localSections.value = props.fieldsets.map(fs => ({
    ...fs,
    fields: enabledFields
      .filter(
        f =>
          f.name !== 'message' && !isBookingField(f) && f.fieldset_id === fs.id
      )
      .map(f => ({ ...f })),
  }));
};

watch(() => [props.preChatFields, props.fieldsets], initLocalState, {
  immediate: true,
});

const emitReorder = () => {
  const flat = [
    ...localUngrouped.value,
    ...localBooking.value,
    ...localSections.value.flatMap(s => s.fields),
  ];
  if (localMessageField.value) {
    flat.push(localMessageField.value);
  }
  emit('reorder', flat);
};

const isElementOnCanvas = el =>
  localUngrouped.value.includes(el) ||
  localBooking.value.includes(el) ||
  localSections.value.some(s => s.fields.includes(el));

const isNameOnCanvas = name => {
  if (!name) return false;
  if (localMessageField.value?.name === name) return true;
  if (localUngrouped.value.some(f => f.name === name)) return true;
  if (localBooking.value.some(f => f.name === name)) return true;
  return localSections.value.some(s => s.fields.some(f => f.name === name));
};

const canMoveField = evt => {
  const el = evt.draggedContext.element;
  if (!el || el.name === 'message') return false;

  // Existing canvas fields can be reordered / moved between sections
  if (isElementOnCanvas(el)) return true;

  // Clones from the picker: block if the field is already on the canvas
  return !isNameOnCanvas(el.name);
};

const removeDuplicateAt = (list, name, newIndex) => {
  if (list[newIndex]?.name === name) {
    list.splice(newIndex, 1);
    return true;
  }
  const idx = list.findIndex(f => f.name === name);
  if (idx !== -1) {
    list.splice(idx, 1);
    return true;
  }
  return false;
};

const moveAddedToBooking = (list, element, newIndex) => {
  list.splice(newIndex, 1);
  if (!localBooking.value.some(f => f.name === element.name)) {
    localBooking.value.push({
      ...element,
      fieldset_id: null,
      enabled: true,
    });
  }
  emit('addField', element);
  emitReorder();
};

const onUngroupedChange = change => {
  if (change.added?.element?.name === 'message') return;
  if (change.added) {
    const { element, newIndex } = change.added;
    if (isBookingField(element)) {
      moveAddedToBooking(localUngrouped.value, element, newIndex);
      return;
    }
    const ungroupedCount = localUngrouped.value.filter(
      f => f.name === element.name
    ).length;
    if (ungroupedCount > 1) {
      removeDuplicateAt(localUngrouped.value, element.name, newIndex);
      return;
    }
    element.fieldset_id = null;
    element.enabled = true;
    emit('addField', element);
  }
  emitReorder();
};

const onBookingChange = change => {
  if (change.added?.element?.name === 'message') return;
  if (change.added) {
    const { element, newIndex } = change.added;
    if (!isBookingField(element)) {
      localBooking.value.splice(newIndex, 1);
      if (!localUngrouped.value.some(f => f.name === element.name)) {
        localUngrouped.value.push({
          ...element,
          fieldset_id: null,
          enabled: true,
        });
      }
      emit('addField', element);
      emitReorder();
      return;
    }
    const bookingCount = localBooking.value.filter(
      f => f.name === element.name
    ).length;
    if (bookingCount > 1) {
      removeDuplicateAt(localBooking.value, element.name, newIndex);
      return;
    }
    element.fieldset_id = null;
    element.enabled = true;
    emit('addField', element);
  }
  emitReorder();
};

const onSectionChange = (fieldsetId, change) => {
  if (change.added?.element?.name === 'message') return;
  if (change.added) {
    const { element, newIndex } = change.added;
    const section = localSections.value.find(s => s.id === fieldsetId);
    if (isBookingField(element)) {
      if (section) {
        moveAddedToBooking(section.fields, element, newIndex);
      }
      return;
    }
    if (section) {
      const sectionCount = section.fields.filter(
        f => f.name === element.name
      ).length;
      if (sectionCount > 1) {
        removeDuplicateAt(section.fields, element.name, newIndex);
        return;
      }
    }
    element.fieldset_id = fieldsetId;
    element.enabled = true;
    emit('addField', element);
  }
  emitReorder();
};

const handleToggleRequired = field => {
  const newRequired = !field.required;
  field.required = newRequired;
  emit('updateField', field, 'required', newRequired);
};

const handleUpdateLabel = (field, value) => {
  field.label = value;
  emit('updateField', field, 'label', value);
};

const handleUpdatePlaceholder = (field, value) => {
  field.placeholder = value;
  emit('updateField', field, 'placeholder', value);
};

const handleUpdateSelectionMode = (field, value) => {
  field.selection_mode = value;
  emit('updateField', field, 'selection_mode', value);
};

const handleUpdateSlotOptions = (field, value) => {
  field.slot_options = Array.isArray(value) ? value : [];
  emit('updateField', field, 'slot_options', field.slot_options);
};

const handleUpdateEnforceAvailability = (field, value) => {
  field.enforce_availability = value !== false;
  emit(
    'updateField',
    field,
    'enforce_availability',
    field.enforce_availability
  );
};

const handleRemove = field => {
  if (field.name === 'message') return;
  emit('removeField', field);
};

const enabledFieldCount = () =>
  (localMessageField.value ? 1 : 0) +
  localUngrouped.value.length +
  localBooking.value.length +
  localSections.value.reduce((sum, s) => sum + s.fields.length, 0);
</script>

<template>
  <div class="flex-1 bg-n-surface-1 rounded-r-xl p-6 overflow-y-auto">
    <!-- Empty-state hint (still allow dropping into the ungrouped list below) -->
    <div
      v-if="enabledFieldCount() === 0 && localSections.length === 0"
      class="flex flex-col items-center justify-center py-10 text-center pointer-events-none"
    >
      <span class="i-lucide-layout-list text-n-slate-9 size-12 mb-4" />
      <h3 class="text-base font-medium text-n-slate-12 mb-1">
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 max-w-sm">
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.EMPTY_STATE.DESCRIPTION') }}
      </p>
    </div>

    <!-- Ungrouped fields (always present as drop target; sections are optional) -->
    <Draggable
      v-model="localUngrouped"
      item-key="name"
      handle=".drag-handle"
      :group="{ name: 'pre-chat-fields' }"
      :move="canMoveField"
      class="flex flex-col gap-2 min-h-[80px]"
      ghost-class="opacity-50"
      @change="onUngroupedChange"
    >
      <template #item="{ element }">
        <FieldCard
          :field="element"
          @toggle-required="handleToggleRequired"
          @update-label="handleUpdateLabel"
          @update-placeholder="handleUpdatePlaceholder"
          @update-selection-mode="handleUpdateSelectionMode"
          @update-slot-options="handleUpdateSlotOptions"
          @update-enforce-availability="handleUpdateEnforceAvailability"
          @remove="handleRemove"
        />
      </template>
    </Draggable>

    <!-- Booking section (auto-grouped; mirrors widget) -->
    <div v-if="localBooking.length" class="mt-4">
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-t-lg bg-n-alpha-1 border border-n-weak"
      >
        <span class="i-lucide-calendar-clock size-4 text-n-slate-10 shrink-0" />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BOOKING_SECTION.TITLE') }}
        </span>
        <span
          v-tooltip.top="t('INBOX_MGMT.PRE_CHAT_FORM.BOOKING_ORDER.HELP')"
          class="i-lucide-info size-3.5 text-n-slate-10 shrink-0 cursor-help"
        />
      </div>
      <Draggable
        v-model="localBooking"
        item-key="name"
        handle=".drag-handle"
        :group="{ name: 'pre-chat-fields' }"
        :move="canMoveField"
        class="flex flex-col gap-2 p-2 min-h-[40px] border border-t-0 border-n-weak rounded-b-lg bg-n-alpha-1/50"
        ghost-class="opacity-50"
        @change="onBookingChange"
      >
        <template #item="{ element }">
          <FieldCard
            :field="element"
            :hide-booking-order-tip="true"
            @toggle-required="handleToggleRequired"
            @update-label="handleUpdateLabel"
            @update-placeholder="handleUpdatePlaceholder"
            @update-selection-mode="handleUpdateSelectionMode"
            @update-slot-options="handleUpdateSlotOptions"
            @update-enforce-availability="handleUpdateEnforceAvailability"
            @remove="handleRemove"
          />
        </template>
      </Draggable>
    </div>

    <!-- Fieldset sections -->
    <div v-for="section in localSections" :key="section.id" class="mt-4">
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-t-lg bg-n-alpha-1 border border-n-weak"
      >
        <IconPicker
          :model-value="section.icon || null"
          @update:model-value="emit('updateFieldsetIcon', section.id, $event)"
        />
        <input
          type="text"
          :value="section.label"
          :placeholder="
            t('INBOX_MGMT.PRE_CHAT_FORM.FIELDSETS.LABEL_PLACEHOLDER')
          "
          class="reset-base flex-1 text-sm font-semibold bg-transparent text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
          @input="emit('updateFieldsetLabel', section.id, $event.target.value)"
        />
        <NextButton
          icon="i-lucide-trash-2"
          size="md"
          color="ruby"
          variant="ghost"
          @click="emit('removeFieldset', section.id)"
        />
      </div>
      <Draggable
        v-model="section.fields"
        item-key="name"
        handle=".drag-handle"
        :group="{ name: 'pre-chat-fields' }"
        :move="canMoveField"
        class="flex flex-col gap-2 p-2 min-h-[40px] border border-t-0 border-n-weak rounded-b-lg bg-n-alpha-1/50"
        ghost-class="opacity-50"
        @change="onSectionChange(section.id, $event)"
      >
        <template #item="{ element }">
          <FieldCard
            :field="element"
            @toggle-required="handleToggleRequired"
            @update-label="handleUpdateLabel"
            @update-placeholder="handleUpdatePlaceholder"
            @update-selection-mode="handleUpdateSelectionMode"
            @update-slot-options="handleUpdateSlotOptions"
            @update-enforce-availability="handleUpdateEnforceAvailability"
            @remove="handleRemove"
          />
        </template>
      </Draggable>
    </div>

    <FieldCard
      v-if="localMessageField"
      class="mt-4"
      :field="localMessageField"
      @toggle-required="handleToggleRequired"
      @update-label="handleUpdateLabel"
      @update-placeholder="handleUpdatePlaceholder"
      @update-selection-mode="handleUpdateSelectionMode"
      @update-slot-options="handleUpdateSlotOptions"
      @update-enforce-availability="handleUpdateEnforceAvailability"
      @remove="handleRemove"
    />

    <!-- Add section button -->
    <div class="mt-4">
      <NextButton
        icon="i-lucide-plus"
        size="sm"
        variant="ghost"
        :label="t('INBOX_MGMT.PRE_CHAT_FORM.FIELDSETS.ADD')"
        @click="emit('addFieldset')"
      />
    </div>

    <!-- Inline submit button preview -->
    <div class="mt-6">
      <div
        class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-white text-sm font-medium"
      >
        <input
          type="text"
          :value="submitButtonLabel"
          :placeholder="
            t('INBOX_MGMT.PRE_CHAT_FORM.SUBMIT_BUTTON_LABEL.PLACEHOLDER')
          "
          class="reset-base bg-transparent text-white placeholder:text-white/60 text-sm font-medium focus:outline-none p-0 [field-sizing:content]"
          @input="emit('updateSubmitLabel', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>
